import '../headline/index.js';
import '../icon/index.js';

import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '../../../lib/component.js';
import styles from './style.scss';
import {styleMap} from 'lit/directives/style-map.js';
import {ModalStatus} from '../../base/modal/index.js';
import type {DownloadType} from '#lit/utils/download.js';
import {download} from '#lit/utils/download.js';

export const COMPONENT_TAG = 'fly-downloader';
const MODAL_TITLE = 'Download CSV';
const MODAL_CAPTION = 'Select the name of the file you want to download';
const MODAL_LABEL = MODAL_TITLE;
const MODAL_OK = 'Download';
const MODAL_CANCEL = 'Back';
const PLACEHOLDER = 'File name';
const DEFAULT_TYPE = 'Audience';

/**
 * FlyDownloader. Modal to trigger CSV downloads
 * It asks for the file name and triggers the download
 * It receives the ids of the items to download an the type of download
 */
@customElement(COMPONENT_TAG)
export default class FlyDownloader extends FlywheelComponent {
  @property()
  title = MODAL_TITLE;

  @property()
  caption = MODAL_CAPTION;

  @property()
  label = MODAL_LABEL;

  @property()
  icon = '';

  @property({type: Boolean})
  activeModal = false;

  @property({type: Object})
  ids: Set<number> = new Set();

  @property()
  name = this.defaultName();

  @property({type: String})
  type: DownloadType = DEFAULT_TYPE;

  static get styles() {
    return [styles];
  }

  load() {
    this.name = this.defaultName();
  }

  defaultName() {
    const date = new Date().toISOString().slice(0, 10);
    return `${this.type || DEFAULT_TYPE}s ${date}`;
  }

  download() {
    download(this.ids, this.type, this.name);
  }

  shouldDisableActions() {
    return this.name === '' || this.ids.size === 0;
  }

  shouldDisableSecondaryAction() {
    return false;
  }

  shouldDisablePrimaryAction() {
    return false;
  }

  onModalToggle(evt: ModalStatus) {
    this.dispatchEvent(new CustomEvent(evt));

    if (evt === ModalStatus.OPENED && !this.activeModal) {
      this.activeModal = true;
      return;
    }

    if (evt === ModalStatus.CLOSED && this.activeModal) {
      this.activeModal = false;
    }
  }

  headerCloseButtonMarkup = () => {
    return html`
      <div slot="header-aside">
        <fly-icon icon="close" size="sm"></fly-icon>
      </div>
    `;
  };

  modalBodyMarkup() {
    return html`
      <div class="downloader-modal">
        <div class="downloader-modal__fields">
          <div class="downloader-modal__fields__label">File Name</div>
          <div class="downloader-modal__fields__input">
            <fly-input
              autofocus
              type="string"
              intent="base"
              value="${this.name}"
              placeholder="${PLACEHOLDER}"
              @change=${(e: Event) => {
                this.name = (e.target as HTMLInputElement).value;
              }}
            >
            </fly-input>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <fly-modal
        async
        actions
        style=${styleMap(styles)}
        ?loading=${this.shouldDisableActions()}
        ?open=${this.activeModal}
        label=${MODAL_TITLE}
        ?preventCloseOnPrimaryAction=${false}
        .okLabel=${MODAL_OK}
        .cancelLabel=${MODAL_CANCEL}
        ?disableSecondaryAction=${this.shouldDisableSecondaryAction()}
        ?disablePrimaryAction=${this.shouldDisablePrimaryAction()}
        @closed=${() => this.onModalToggle(ModalStatus.CLOSED)}
        @opened=${() => this.onModalToggle(ModalStatus.OPENED)}
        @cancel=${() => this.onModalToggle(ModalStatus.CLOSED)}
        @ok=${() => {
          this.download();
          this.onModalToggle(ModalStatus.CLOSED);
        }}
      >
        ${this.headerCloseButtonMarkup()} ${this.modalBodyMarkup()}
      </fly-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fly-downloader': FlyDownloader;
  }
}
